 import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { UserProfile, defaultProfile } from '@/types/profile';
 import { supabase } from '@/integrations/supabase/client';
 import { AuthContext } from '@/contexts/AuthContext';

interface ProfileContextType {
  profile: UserProfile;
  updateProfile: (updates: Partial<UserProfile>) => void;
  resetProfile: () => void;
  isProfileComplete: boolean;
   loading: boolean;
   syncProfile: () => Promise<void>;
   /** Immediately flush any pending DB writes (bypasses debounce) */
   flushDbUpdates: () => Promise<void>;
   /** Last DB sync error message, or null if last save succeeded */
   saveError: string | null;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

const STORAGE_KEY = 'user-profile';

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
   // Use useContext directly (instead of the throwing useAuth() hook) so that
  // ProfileProvider survives React Refresh / HMR when AuthProvider hasn't
  // re-mounted yet. In production the providers are always properly nested.
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user ?? null;
   const [loading, setLoading] = useState(true);
   const [saveError, setSaveError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return defaultProfile;
      }
    }
    return defaultProfile;
  });

   // Load profile from database when user logs in
  useEffect(() => {
     const loadProfile = async () => {
       if (user) {
         setLoading(true);
         const { data, error } = await supabase
           .from('profiles')
           .select('*')
           .eq('user_id', user.id)
           .single();
         
         if (!error && data) {
           // Resolve account role: DB column → user metadata fallback
           const dbRole = (data as any).account_role as 'student' | 'parent' | null;
           const metaRole = user.user_metadata?.role as 'student' | 'parent' | undefined;
           const resolvedRole = dbRole || metaRole || undefined;

           const dbProfile: UserProfile = {
             ...defaultProfile,
             firstName: data.first_name || '',
             lastName: data.last_name || '',
             email: data.email || user.email || '',
             gpa: Number(data.gpa) || 0,
             gradeLevel: (data.current_grade_level as any) || defaultProfile.gradeLevel,
             intendedMajor: data.intended_major || '',
             majorCategory: (data.major_category as any) || defaultProfile.majorCategory,
             state: data.state || '',
             ethnicity: (data.ethnicity as any[]) || [],
             gender: (data.gender_identity as any) || defaultProfile.gender,
             financialNeed: (data.household_income as any) === 'under-30k' || (data.household_income as any) === '30k-60k' ? 'high' : defaultProfile.financialNeed,
             pellGrantEligible: data.pell_grant_eligible || false,
             fafsaComplete: data.fafsa_completed || false,
             householdIncome: (data.household_income as any) || '60k-100k',
             firstGeneration: data.first_gen || false,
             citizenship: (data.citizenship_status as any) || 'us-citizen',
             extracurriculars: (data.extracurriculars as any[]) || [],
             careerInterests: data.intended_career ? [data.intended_career] : [],
             avatar: (data as any).avatar || undefined,
             isComplete: data.is_complete || false,
             completedAt: data.completed_at || undefined,
             onboardingCompleted: (data as any).onboarding_completed || false,
             accountRole: resolvedRole,
             parentUserId: (data as any).parent_user_id || undefined,
           };
           setProfile(dbProfile);
           localStorage.setItem(STORAGE_KEY, JSON.stringify(dbProfile));

           // If DB column is empty but metadata has role, sync it to DB
           if (!dbRole && metaRole) {
             supabase
               .from('profiles')
               .update({ account_role: metaRole } as any)
               .eq('user_id', user.id)
               .then(() => { /* best-effort sync */ });
           }
         }
         setLoading(false);
       } else {
         // Not logged in, use localStorage
         setLoading(false);
       }
     };
 
     loadProfile();
   }, [user]);

   // Sync profile across tabs via storage event
   useEffect(() => {
     const handleStorage = (e: StorageEvent) => {
       if (e.key === STORAGE_KEY && e.newValue) {
         try {
           setProfile(JSON.parse(e.newValue));
         } catch { /* ignore parse errors */ }
       }
     };
     window.addEventListener('storage', handleStorage);
     return () => window.removeEventListener('storage', handleStorage);
   }, []);

   // Debounced DB sync: accumulate field changes and flush after 1s of inactivity
   const pendingDbUpdates = useRef<Record<string, any>>({});
   const dbFlushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

   const flushDbUpdates = useCallback(async () => {
     if (!user) return;
     const updates = { ...pendingDbUpdates.current };
     pendingDbUpdates.current = {};
     if (Object.keys(updates).length === 0) return;

     const { error } = await supabase
       .from('profiles')
       .update(updates)
       .eq('user_id', user.id);

     if (error) {
       console.error('[ProfileContext] Failed to save profile to DB:', error);
       setSaveError('Profile changes could not be saved. Please try again.');
     } else {
       setSaveError(null);
     }
   }, [user]);

   // Flush pending DB writes on unmount
   useEffect(() => {
     return () => {
       if (dbFlushTimer.current) clearTimeout(dbFlushTimer.current);
       // Best-effort flush — fire and forget
       if (Object.keys(pendingDbUpdates.current).length > 0 && user) {
         flushDbUpdates();
       }
     };
   }, [flushDbUpdates, user]);

   // Persist to localStorage immediately, debounce DB writes
   const updateProfile = useCallback((updates: Partial<UserProfile>) => {
     setProfile((prev) => {
      const updated = { ...prev, ...updates };
      // Only recalculate isComplete if it's explicitly being set OR was previously false.
      // Never downgrade from true → false just because a field is temporarily empty.
      if ('isComplete' in updates) {
        updated.isComplete = updates.isComplete!;
      } else if (!prev.isComplete) {
        updated.isComplete = checkProfileComplete(updated);
      }
      // If prev.isComplete was already true, keep it true (don't recalculate)
      if (updated.isComplete && !prev.isComplete) {
        updated.completedAt = new Date().toISOString();
      }
       localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });

     // Accumulate DB field changes
     if (user) {
       const dbUpdates: Record<string, any> = {};
       if ('firstName' in updates) dbUpdates.first_name = updates.firstName;
       if ('lastName' in updates) dbUpdates.last_name = updates.lastName;
       if ('email' in updates) dbUpdates.email = updates.email;
       if ('gpa' in updates) dbUpdates.gpa = updates.gpa;
       if ('gradeLevel' in updates) dbUpdates.current_grade_level = updates.gradeLevel;
       if ('intendedMajor' in updates) dbUpdates.intended_major = updates.intendedMajor;
       if ('majorCategory' in updates) dbUpdates.major_category = updates.majorCategory;
       if ('state' in updates) dbUpdates.state = updates.state;
       if ('ethnicity' in updates) dbUpdates.ethnicity = updates.ethnicity;
       if ('gender' in updates) dbUpdates.gender_identity = updates.gender;
       if ('pellGrantEligible' in updates) dbUpdates.pell_grant_eligible = updates.pellGrantEligible;
       if ('fafsaComplete' in updates) dbUpdates.fafsa_completed = updates.fafsaComplete;
       if ('householdIncome' in updates) dbUpdates.household_income = updates.householdIncome;
       if ('firstGeneration' in updates) dbUpdates.first_gen = updates.firstGeneration;
       if ('citizenship' in updates) dbUpdates.citizenship_status = updates.citizenship;
       if ('extracurriculars' in updates) dbUpdates.extracurriculars = updates.extracurriculars;
       if ('careerInterests' in updates) dbUpdates.intended_career = updates.careerInterests?.join(', ') || null;
       if ('avatar' in updates) dbUpdates.avatar = updates.avatar;
       if ('isComplete' in updates) dbUpdates.is_complete = updates.isComplete;
       if ('completedAt' in updates) dbUpdates.completed_at = updates.completedAt;
       if ('accountRole' in updates) dbUpdates.account_role = updates.accountRole;
       if ('parentUserId' in updates) dbUpdates.parent_user_id = updates.parentUserId;
       if ('onboardingCompleted' in updates) dbUpdates.onboarding_completed = updates.onboardingCompleted;

       if (Object.keys(dbUpdates).length > 0) {
         // Merge into pending and reset debounce timer
         Object.assign(pendingDbUpdates.current, dbUpdates);
         if (dbFlushTimer.current) clearTimeout(dbFlushTimer.current);
         dbFlushTimer.current = setTimeout(flushDbUpdates, 1000);
       }
     }
   }, [user, flushDbUpdates]);
 
   const syncProfile = useCallback(async () => {
     if (!user) return;
     
     // Upload current localStorage profile to database
     const stored = localStorage.getItem(STORAGE_KEY);
     if (stored) {
       try {
         const localProfile = JSON.parse(stored) as UserProfile;
         await supabase
           .from('profiles')
           .update({
             first_name: localProfile.firstName,
             last_name: localProfile.lastName,
             email: localProfile.email || user.email,
             gpa: localProfile.gpa,
             current_grade_level: localProfile.gradeLevel,
             intended_major: localProfile.intendedMajor,
             major_category: localProfile.majorCategory,
             state: localProfile.state,
             ethnicity: localProfile.ethnicity,
             gender_identity: localProfile.gender,
             pell_grant_eligible: localProfile.pellGrantEligible,
             fafsa_completed: localProfile.fafsaComplete,
             household_income: localProfile.householdIncome,
             first_gen: localProfile.firstGeneration,
             citizenship_status: localProfile.citizenship,
             extracurriculars: localProfile.extracurriculars,
             intended_career: localProfile.careerInterests?.join(', ') || null,
             avatar: localProfile.avatar,
             is_complete: localProfile.isComplete,
             completed_at: localProfile.completedAt,
             account_role: localProfile.accountRole,
             parent_user_id: localProfile.parentUserId,
             onboarding_completed: localProfile.onboardingCompleted,
           } as any)
           .eq('user_id', user.id);
       } catch {
         // Ignore parse errors
       }
     }
   }, [user]);

  const resetProfile = () => {
    setProfile(defaultProfile);
    localStorage.removeItem(STORAGE_KEY);
  };

  const checkProfileComplete = (p: UserProfile): boolean => {
    // Simplified onboarding (v2) collects: firstName, gradeLevel, majorCategory, citizenship.
    // Only firstName defaults to '' (falsy) — it's the most reliable "user has engaged" signal.
    // gradeLevel defaults to 'senior', majorCategory to 'undecided', citizenship to 'us-citizen'
    // (all truthy), so they can't distinguish "never set" from "explicitly chosen default".
    // Existing users with isComplete=true are unaffected — the code never downgrades.
    return !!(p.firstName);
  };

  return (
    <ProfileContext.Provider
      value={{
        profile,
        updateProfile,
        resetProfile,
        isProfileComplete: profile.isComplete,
         loading,
         syncProfile,
         flushDbUpdates,
         saveError,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};
